import {useEffect, useRef, useState} from 'react'
import {Modal, Button, Form, Row, Col, Nav} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import Select from 'react-select'
import {useQuery} from '@apollo/client'
import {GENDER_OPTIONS, INITIAL_CONTACT_FILTERS} from '../../../helpers/consts'
import {ContactFilters, ContactSavedFilter} from '../contactsTypes'
import {parseId} from '../../../helpers'
import {GET_CONTACT_TAGS} from '../../../gql/queries/tags'

interface ModalFiltersProps {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: any) => void
  filters: ContactFilters
  setFilters: React.Dispatch<React.SetStateAction<ContactFilters>>
  savedFilters?: ContactSavedFilter[]
  onSaveFilter?: (name: string, filters: ContactFilters) => Promise<ContactSavedFilter | null>
  onUpdateFilter?: (id: string, name: string, filters: ContactFilters) => Promise<ContactSavedFilter | null>
  onDeleteFilter?: (id: string) => Promise<boolean>
  users: any[]
  states: any[]
  immigrationStatuses: { label: string; value: string }[]
  signatureForms: any[]
  carriers: any[]
  policyStatuses: { id: string; status: string }[]
  allowedImmigrationStatuses?: string[]
  hideImmigrationStatus?: boolean
}

export const ModalFilters: React.FC<ModalFiltersProps> = ({
  isOpen,
  onClose,
  onApply,
  filters,
  setFilters,
  savedFilters = [],
  onSaveFilter,
  onUpdateFilter,
  onDeleteFilter,
  users,
  states,
  immigrationStatuses,
  signatureForms,
  carriers,
  policyStatuses,
  allowedImmigrationStatuses,
  hideImmigrationStatus = false,
}) => {
  const intl = useIntl()
  const selectPlaceholder = intl.formatMessage({
    id: 'common.placeholder.selectOption',
    defaultMessage: 'Select an option',
  })
  const [activeTab, setActiveTab] = useState<'filters' | 'saved'>('filters')
  const [savedTabMode, setSavedTabMode] = useState<'save' | 'manage'>('manage')

  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null)
  const [savedFilterName, setSavedFilterName] = useState('')
  const [shouldFocusSavedName, setShouldFocusSavedName] = useState(false)
  const savedNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (!selectedSavedFilterId) {
      setSavedFilterName('')
    }
  }, [isOpen, selectedSavedFilterId])

  useEffect(() => {
    if (activeTab !== 'saved' || !shouldFocusSavedName) return
    if (savedNameInputRef.current) {
      savedNameInputRef.current.focus()
      savedNameInputRef.current.select()
      setShouldFocusSavedName(false)
    }
  }, [activeTab, shouldFocusSavedName])

  const goToSavedTab = (mode: 'save' | 'manage') => {
    setActiveTab('saved')
    setSavedTabMode(mode)
    setShouldFocusSavedName(mode === 'save')
  }

  const handleSelectChange = (field: string, selected: any) => {
    let value = selected?.value
    if (value === 'true') value = true
    else if (value === 'false') value = false
    else if (value === '') value = undefined

    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleInputChange = (field: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleImmigrationStatusChange = (selected: any) => {
    const values = Array.isArray(selected)
      ? selected.map((opt: any) => opt?.value).filter(Boolean)
      : selected?.value
        ? [selected.value]
        : []

    setFilters((prev: any) => ({
      ...prev,
      immigrationStatus: values,
    }))
  }

  const handleSubmit = () => {
    const parsedFilters = {
      ...filters,
      ...(filters.carrier && { carrier: parseId(filters.carrier) }),
      ...(filters.signatureForm && { signatureForm: parseId(filters.signatureForm) }),
      ...(filters.policyType && { policyType: parseId(filters.policyType) }),
    }

    console.log('🚀 Filtros aplicados (parseados) desde el modal:', parsedFilters)
    onApply(parsedFilters)
    onClose()
  }

  const usersOptions = users.map((u) => ({value: u.id, label: `${u.fullName} - ${u.email}`}))
  const selectedUser = usersOptions.find((opt) => String(opt.value) === String(filters.user)) || null

  const languageOptions = [
    {value: 'Spanish', label: intl.formatMessage({id: 'common.language.spanish', defaultMessage: 'Spanish'})},
    {value: 'English', label: intl.formatMessage({id: 'common.language.english', defaultMessage: 'English'})},
  ]
  const selectedLanguage =
    languageOptions.find((opt) => opt.value === filters.preferredLanguage) || null

  const genderLabelIds: Record<string, {id: string; defaultMessage: string}> = {
    Male: {id: 'common.gender.male', defaultMessage: 'Male'},
    Female: {id: 'common.gender.female', defaultMessage: 'Female'},
    Other: {id: 'common.option.other', defaultMessage: 'Other'},
  }
  const genderOptions = GENDER_OPTIONS.map((s) => ({
    value: s.value,
    label: genderLabelIds[s.value] ? intl.formatMessage(genderLabelIds[s.value]) : s.label,
  }))
  const selectedGender = genderOptions.find((opt) => opt.value === filters.gender) || null

  const {data: tagsData} = useQuery(GET_CONTACT_TAGS, {skip: !isOpen})
  const tagOptions = (tagsData?.contactTags || []).map((t: any) => ({value: t.id, label: t.name, color: t.color}))
  const selectedTagIds = filters.tags ? filters.tags.split(',').filter(Boolean) : []
  const selectedTagOptions = tagOptions.filter((opt: any) => selectedTagIds.includes(opt.value))

  const stateOptions = states.map((s) => ({value: s.code, label: s.name}))
  const selectedState = stateOptions.find((opt) => opt.value === filters.state) || null

  const immigrationOptions = allowedImmigrationStatuses?.length
    ? immigrationStatuses.filter((opt) => allowedImmigrationStatuses.includes(opt.value))
    : immigrationStatuses
  const selectedImmigrationValues = Array.isArray(filters.immigrationStatus)
    ? filters.immigrationStatus
    : filters.immigrationStatus
      ? [filters.immigrationStatus]
      : []
  const selectedImmigration = immigrationOptions.filter((opt) =>
    selectedImmigrationValues.includes(opt.value)
  )

  const signatureStatusOptions = [
    {value: 'SIGNED', label: intl.formatMessage({id: 'common.status.signed', defaultMessage: 'Signed'})},
    {value: 'NOT_SIGNED', label: intl.formatMessage({id: 'contacts.filters.status.notSigned', defaultMessage: 'Did not sign'})},
  ]
  const selectedSignatureStatus =
    signatureStatusOptions.find((opt) => opt.value === filters.signatureStatus) || null

  const signatureFormOptions = signatureForms.map((s) => ({value: s.id, label: s.name}))
  const selectedSignatureForm =
    signatureFormOptions.find((opt) => String(opt.value) === String(filters.signatureForm)) || null

  const carrierOptions = carriers.map((c) => ({value: c.id, label: c.name}))
  const selectedCarrier =
    carrierOptions.find((opt) => String(opt.value) === String(filters.carrier)) || null

  const policyStatusOptions = policyStatuses.map((s) => ({value: s.status, label: s.status}))
  const selectedPolicyStatus =
    policyStatusOptions.find((opt) => opt.value === filters.policyStatus) || null

  const savedFilterOptions = savedFilters.map((filter) => ({
    value: filter.id,
    label: filter.name,
  }))

  const selectedSavedFilterOption =
    savedFilterOptions.find((opt) => opt.value === selectedSavedFilterId) || null

  const handleSavedFilterSelect = (selected: any) => {
    if (!selected) {
      setSelectedSavedFilterId(null)
      setSavedFilterName('')
      return
    }

    const match = savedFilters.find((filter) => filter.id === selected.value)
    if (!match) return

    setSelectedSavedFilterId(match.id)
    setSavedFilterName(match.name)
    setFilters(() => ({...match.filters}))
  }

  const handleSaveFilter = async () => {
    if (!onSaveFilter) return
    const saved = await onSaveFilter(savedFilterName, filters)
    if (!saved) return
    setSelectedSavedFilterId(saved.id)
    setSavedFilterName(saved.name)
  }

  const handleSaveAndApply = async () => {
    if (onSaveFilter) {
      const saved = await onSaveFilter(savedFilterName, filters)
      if (!saved) return
      setSelectedSavedFilterId(saved.id)
      setSavedFilterName(saved.name)
    }
    handleSubmit()
  }

  const handleUpdateFilter = async () => {
    if (!onUpdateFilter || !selectedSavedFilterId) return
    const saved = await onUpdateFilter(selectedSavedFilterId, savedFilterName, filters)
    if (!saved) return
    setSavedFilterName(saved.name)
  }

  const handleDeleteFilter = async () => {
    if (!onDeleteFilter || !selectedSavedFilterId) return
    const confirmed = window.confirm(
      intl.formatMessage({
        id: 'contacts.filters.confirmDelete',
        defaultMessage: 'Delete this saved filter?',
      })
    )
    if (!confirmed) return
    const deleted = await onDeleteFilter(selectedSavedFilterId)
    if (!deleted) return
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
  }

  const handleClearFilters = () => {
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
    setFilters(() => ({...INITIAL_CONTACT_FILTERS}))
  }

  const handleApplySavedFilter = () => {
    if (!selectedSavedFilterId) return
    onApply(filters)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='xl'>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='contacts.filters.title' defaultMessage='Advanced Filters' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex align-items-center justify-content-between mb-3 border-bottom pb-2'>
          <Nav
            variant='tabs'
            className='border-bottom-0'
            activeKey={activeTab}
            onSelect={(key) => {
              const nextTab = (key as 'filters' | 'saved') || 'filters'
              setActiveTab(nextTab)
              if (nextTab === 'saved') {
                setSavedTabMode('manage')
              }
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey='filters'>
                <FormattedMessage id='contacts.filters.tab.filters' defaultMessage='Filters' />
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='saved'>
                <FormattedMessage id='contacts.filters.tab.saved' defaultMessage='Saved filters' />
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab === 'filters' ? (
            <div className='d-flex gap-2'>
              <Button variant='light' className='px-3' onClick={handleClearFilters}>
                <FormattedMessage id='contacts.filters.clear' defaultMessage='Clear' />
              </Button>
              <Button variant='dark' className='px-3' onClick={() => goToSavedTab('save')}>
                <i className='bi bi-save me-1' />
                <FormattedMessage id='contacts.filters.saveFilter' defaultMessage='Save Filter' />
              </Button>
              <Button variant='primary' className='px-3' onClick={handleSubmit}>
                <i className='bi bi-search me-1' />
                <FormattedMessage id='contacts.filters.apply' defaultMessage='Apply Filters' />
              </Button>
            </div>
          ) : null}
        </div>
        <Form>
          {activeTab === 'saved' ? (
            <>
              <div className='mb-2'>
                <h5 className='mb-1'>
                  <FormattedMessage id='contacts.filters.manageTitle' defaultMessage='Manage saved filters' />
                </h5>
                {savedTabMode === 'manage' ? (
                  <div className='text-muted'>
                    <FormattedMessage
                      id='contacts.filters.manageHint'
                      defaultMessage='Select a saved filter for quick access to data'
                    />
                  </div>
                ) : null}
              </div>
              {savedTabMode === 'save' ? (
                <Row className='mb-3 align-items-end'>
                  <Col md={8}>
                    <Form.Label>
                      <FormattedMessage id='contacts.filters.nameLabel' defaultMessage='Filter name' />
                    </Form.Label>
                    <Form.Control
                      ref={savedNameInputRef}
                      value={savedFilterName}
                      placeholder={intl.formatMessage({id: 'contacts.filters.namePlaceholder', defaultMessage: 'Enter filter name'})}
                      onChange={(e) => setSavedFilterName(e.target.value)}
                    />
                    <div className='text-muted mt-2'>
                      <FormattedMessage
                        id='contacts.filters.saveHint'
                        defaultMessage='Save the current filters so you can reuse them later.'
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Label>&nbsp;</Form.Label>
                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleSaveAndApply}
                        disabled={!savedFilterName.trim()}
                      >
                        <FormattedMessage id='contacts.filters.saveAndApply' defaultMessage='Save and apply' />
                      </Button>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row className='mb-2 align-items-center'>
                  <Col md={8}>
                    <Form.Label>
                      <FormattedMessage id='contacts.filters.savedLabel' defaultMessage='Saved Filters' />
                    </Form.Label>
                    <Select
                      value={selectedSavedFilterOption}
                      options={savedFilterOptions}
                      onChange={handleSavedFilterSelect}
                      isClearable
                      placeholder={intl.formatMessage({id: 'contacts.filters.selectSavedPlaceholder', defaultMessage: 'Select saved filter'})}
                    />
                  </Col>
                  <Col md={4}>
                    <div className='d-flex flex-wrap justify-content-end gap-2'>
                      <Button variant='light' className='px-3' onClick={onClose}>
                        <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                      </Button>
                      <Button
                        variant='outline-danger'
                        className='px-3'
                        onClick={handleDeleteFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                      </Button>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleApplySavedFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        <FormattedMessage id='contacts.filters.applySaved' defaultMessage='Apply saved filter' />
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
              <hr />
            </>
          ) : null}
          {activeTab === 'filters' ? (
            <>
              <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='common.field.user' defaultMessage='User' />
              </Form.Label>
              <Select
                value={selectedUser}
                options={usersOptions}
                onChange={(selected) => handleSelectChange('user', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.profile.field.preferredLanguage' defaultMessage='Preferred Language' />
              </Form.Label>
              <Select
                value={selectedLanguage}
                options={languageOptions}
                onChange={(selected) => handleSelectChange('preferredLanguage', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.birthMonth' defaultMessage='Date of Birth Month' />
              </Form.Label>
              <Form.Select
                value={filters.birthMonth}
                onChange={(e) => handleInputChange('birthMonth', e.target.value)}
              >
                <option value=''>
                  {intl.formatMessage({id: 'contacts.filters.selectOne', defaultMessage: 'Select one'})}
                </option>
                {[
                  {id: 'common.month.january', defaultMessage: 'January'},
                  {id: 'common.month.february', defaultMessage: 'February'},
                  {id: 'common.month.march', defaultMessage: 'March'},
                  {id: 'common.month.april', defaultMessage: 'April'},
                  {id: 'common.month.may', defaultMessage: 'May'},
                  {id: 'common.month.june', defaultMessage: 'June'},
                  {id: 'common.month.july', defaultMessage: 'July'},
                  {id: 'common.month.august', defaultMessage: 'August'},
                  {id: 'common.month.september', defaultMessage: 'September'},
                  {id: 'common.month.october', defaultMessage: 'October'},
                  {id: 'common.month.november', defaultMessage: 'November'},
                  {id: 'common.month.december', defaultMessage: 'December'},
                ].map((month, index) => (
                  <option key={index} value={index + 1}>{intl.formatMessage(month)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.ageFrom' defaultMessage='Age from' />
              </Form.Label>
              <Form.Control
                type='number'
                value={filters.ageFrom}
                onChange={(e) => handleInputChange('ageFrom', e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.ageTo' defaultMessage='Age to' />
              </Form.Label>
              <Form.Control
                type='number'
                value={filters.ageTo}
                onChange={(e) => handleInputChange('ageTo', e.target.value)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='common.field.gender' defaultMessage='Gender' />
              </Form.Label>
              <Select
                value={selectedGender}
                options={genderOptions}
                onChange={(selected) => handleSelectChange('gender', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='common.field.state' defaultMessage='State' />
              </Form.Label>
              <Select
                value={selectedState}
                options={stateOptions}
                onChange={(selected) => handleSelectChange('state', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='common.field.zipCode' defaultMessage='Zip Code' />
              </Form.Label>
              <Form.Control
                type='text'
                value={filters.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.tags' defaultMessage='Tags' />
              </Form.Label>
              <Select
                value={selectedTagOptions}
                options={tagOptions}
                onChange={(selected: any) =>
                  handleInputChange('tags', selected ? selected.map((s: any) => s.value).join(',') : '')
                }
                isMulti
                closeMenuOnSelect={false}
                placeholder={intl.formatMessage({id: 'contacts.filters.tagsPlaceholder', defaultMessage: 'Filter by tags...'})}
                formatOptionLabel={(opt: any) => (
                  <span className='d-flex align-items-center gap-2'>
                    <span style={{width: 10, height: 10, borderRadius: '50%', backgroundColor: opt.color, display: 'inline-block', flexShrink: 0}} />
                    {opt.label}
                  </span>
                )}
              />
            </Col>
          </Row>

          {!hideImmigrationStatus && (
            <Row className='mb-3'>
              <Col md={12}>
                <Form.Label>
                  <FormattedMessage id='contacts.profile.field.immigrationStatus' defaultMessage='Immigration Status' />
                </Form.Label>
                <Select
                  value={selectedImmigration}
                  options={immigrationOptions}
                  onChange={handleImmigrationStatusChange}
                  placeholder={selectPlaceholder}
                  isMulti
                  closeMenuOnSelect={false}
                />
              </Col>
            </Row>
          )}

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.createdFrom' defaultMessage='Contact Created From' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.createdAfter}
                onChange={(e) => handleInputChange('createdAfter', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.createdTo' defaultMessage='Contact Created To' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.createdBefore}
                onChange={(e) => handleInputChange('createdBefore', e.target.value)}
              />
            </Col>
          </Row>

          <hr />
          <h5>
            <FormattedMessage id='contacts.filters.section.signature' defaultMessage='Signature Form Filters' />
          </h5>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.signatureStatus' defaultMessage='Signature Form Status' />
              </Form.Label>
              <Select
                value={selectedSignatureStatus}
                options={signatureStatusOptions}
                onChange={(selected) => handleSelectChange('signatureStatus', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.signatureForm' defaultMessage='Signature Form' />
              </Form.Label>
              <Select
                value={selectedSignatureForm}
                options={signatureFormOptions}
                onChange={(selected) => handleSelectChange('signatureForm', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
          </Row>

          <hr />
          <h5>
            <FormattedMessage id='contacts.filters.section.policy' defaultMessage='Policy Filters' />
          </h5>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.carrier' defaultMessage='Carrier' />
              </Form.Label>
              <Select
                value={selectedCarrier}
                options={carrierOptions}
                onChange={(selected) => handleSelectChange('carrier', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.policyStatus' defaultMessage='Policy Status' />
              </Form.Label>
              <Select
                value={selectedPolicyStatus}
                options={policyStatusOptions}
                onChange={(selected) => handleSelectChange('policyStatus', selected)}
                placeholder={selectPlaceholder}
                isClearable
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.effectiveFrom' defaultMessage='Effective Date From' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.effectiveDateAfter}
                onChange={(e) => handleInputChange('effectiveDateAfter', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.effectiveTo' defaultMessage='Effective Date To' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.effectiveDateBefore}
                onChange={(e) => handleInputChange('effectiveDateBefore', e.target.value)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.policyCreatedFrom' defaultMessage='Created Date From' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.policyCreatedFrom}
                onChange={(e) => handleInputChange('policyCreatedFrom', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.policyCreatedTo' defaultMessage='Created Date To' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.policyCreatedTo}
                onChange={(e) => handleInputChange('policyCreatedTo', e.target.value)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.submissionFrom' defaultMessage='Submission Date From' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.submissionDateFrom}
                onChange={(e) => handleInputChange('submissionDateFrom', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FormattedMessage id='contacts.filters.field.submissionTo' defaultMessage='Submission Date To' />
              </Form.Label>
              <Form.Control
                type='date'
                value={filters.submissionDateTo}
                onChange={(e) => handleInputChange('submissionDateTo', e.target.value)}
              />
            </Col>
          </Row>
            </>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {activeTab === 'filters' ? (
          <>
            <Button variant='light' className='px-4' onClick={onClose}>
              <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
            </Button>
            <Button variant='dark' className='px-4' onClick={handleSubmit}>
              <i className='bi bi-search me-1' />
              <FormattedMessage id='contacts.filters.apply' defaultMessage='Apply Filters' />
            </Button>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  )
}
